import React, { useCallback, useRef, useState } from 'react';

import { useServicePromoCodeValidate } from 'features/promotions/hooks/use-loyalty-service-validate-promotion-code';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { BottomSheet, KeyboardAwareBottomSheetContent } from 'components/bottom-sheet';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import {
  ContentHeader,
  ErrorLabel,
  InputWrapper,
  StyledFormControl,
  StyledInput,
} from 'pages/loyalty/loyalty-offers/loyalty-promo-code/loyalty-promo-code-drawer.styled';
import {
  PromoCodeError,
  PromoCodeErrorReason,
} from 'pages/loyalty/loyalty-offers/loyalty-promo-code/types';
import {
  buildErrorMessageFromPromoCodeError,
  createRbiApplyPromoCodeEvent,
} from 'pages/loyalty/loyalty-offers/loyalty-promo-code/utils';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { AllowedEvent } from 'state/amplitude/allowed-events';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getDesignToken } from 'styles/constants';

interface ILoyaltyServicePromoCodeDrawerProps {
  isOpen: boolean;
  onDismiss: () => void;
  promoCode?: string;
  onPromoCodeRedeemSuccess?: (loyaltyEngineId: string) => void;
  onAuthenticationRequired: (promoCode: string) => void;
}

export const LoyaltyServicePromoCodeDrawer = ({
  isOpen,
  onDismiss,
  promoCode,
  onPromoCodeRedeemSuccess,
  onAuthenticationRequired,
}: ILoyaltyServicePromoCodeDrawerProps) => {
  const { loyaltyUser } = useLoyaltyUser();
  const [promoCodeValue, setPromoCodeValue] = useState(promoCode ?? '');
  const { validatePromoCode } = useServicePromoCodeValidate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<PromoCodeError | null>(null);
  const { isAuthenticated } = useAuthContext();
  // Using ref to autofocus input due to Android not supporting autoFocus prop
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOnApplyPromoCode = useCallback(
    async (code: string) => {
      if (!isAuthenticated || !loyaltyUser?.id) {
        // dismiss the promo drawer
        onAuthenticationRequired(code);

        return;
      }

      setLoading(true);
      setError(null);

      let status: 'Successful' | 'Failed' = 'Successful';
      let event = createRbiApplyPromoCodeEvent(promoCodeValue, status);
      let failureDetails: PromoCodeErrorReason | undefined;
      let validateResult: { personalizedOfferId: string; configId: string } | undefined;

      try {
        validateResult = await validatePromoCode(code);
      } catch (e) {
        const promoCodeError =
          e instanceof PromoCodeError ? e : new PromoCodeError(PromoCodeErrorReason.invalidCode);
        failureDetails = promoCodeError.message;
        status = 'Failed';
        event = createRbiApplyPromoCodeEvent(code, status, failureDetails);
        setError(promoCodeError);
      } finally {
        setLoading(false);
      }

      logRBIEvent(event);

      const eventData: AllowedEvent = {
        name: CustomEventNames.PROMO_CODE_APPLIED,
        type: EventTypes.Other,
        attributes: {
          failureDetails,
          promoCode: code,
          status,
          ...(validateResult && {
            incentiveType: LoyaltyPromotionType.OFFER,
            loyaltyEngineId: validateResult.configId,
            engineId: validateResult.configId,
          }),
        },
      };

      logRBIEvent(eventData, { skipLoggingToBraze: true });

      if (status === 'Successful' && validateResult) {
        onPromoCodeRedeemSuccess?.(validateResult.personalizedOfferId);
        onDismiss();
      }
    },
    [
      isAuthenticated,
      loyaltyUser?.id,
      promoCodeValue,
      validatePromoCode,
      onPromoCodeRedeemSuccess,
      onDismiss,
      onAuthenticationRequired,
    ]
  );

  return (
    <BottomSheet isOpen={isOpen} onClose={onDismiss} onOpenEventMessage="Apply Promo Code">
      <KeyboardAwareBottomSheetContent>
        <ContentHeader>{t('applyPromoCode')}</ContentHeader>
        <StyledFormControl isInvalid={!!error}>
          <InputWrapper>
            <StyledInput
              //This nasty hack enables keyboard opening on Android on input focus because `autoFocus` attribute doesn't work on all android
              onLayout={() => setTimeout(() => inputRef.current?.focus(), 0)}
              ref={inputRef}
              value={promoCodeValue}
              onChangeText={setPromoCodeValue}
              _focus={{
                borderColor: error ? getDesignToken('border-color-error') : undefined,
              }}
              // Super hacky way of getting border color to update on Native Base 3.3.4
              InputRightElement={<></>}
            />

            <ActionButton
              disabled={!promoCodeValue || loading}
              variant={ActionButtonVariants.OUTLINE}
              onPress={() => {
                handleOnApplyPromoCode(promoCodeValue);
              }}
            >
              {t('apply')}
            </ActionButton>
          </InputWrapper>

          {!!error && <ErrorLabel>{buildErrorMessageFromPromoCodeError(error)}</ErrorLabel>}
        </StyledFormControl>
      </KeyboardAwareBottomSheetContent>
    </BottomSheet>
  );
};
